/* eslint-disable @typescript-eslint/no-explicit-any */
// "From your care team" — the KERNEL-backed part of the notifications screen.
//
// ⭐ WHY THIS IS A SEPARATE BLOCK AND NOT A MERGE INTO THE EXISTING LIST. The notifications
// screen's list is blaze's `notifications` table, delivered over socket.io. Its rows carry a
// `type`, a `title` and a `message`; kernel engagement items carry a thread and a survey with
// their own actions. Interleaving them would need a synthetic row shape and a second source of
// truth for "unread", and blaze's own unread count is hardcoded to 0 today (see
// `.arc-board/PATIENT-TOUCHPOINTS.md`). Two clearly-labelled sections tell the truth; one
// merged list would not.
//
// ⛔ THERE IS NO PUSH FOR THESE, BY OWNER RULING (E12d/R1: "in-app only for now, push later").
// The kernel's `post_staff_message` reaches a notify SEAM whose only implementation delivers
// nothing. This block, the badge on Settings > CARE TEAM > Messages and the badge on each
// thread are how a patient finds out — which is why it leads the screen rather than sitting at
// the bottom.
//
// ⭐ ONE CALL, NOT THREE. `useEngagementSync` posts `/me/engagement/sync`, which also ISSUES any
// survey that has fallen due (E12d/R2 — there is no scheduler on the kernel). Three separate
// GETs painted this block from three moments in time and could not issue anything.

import React, { useRef } from "react";
import {
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import {
  chatbubbleEllipsesOutline,
  documentTextOutline,
  happyOutline,
} from "ionicons/icons";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
import { useEngagementSync } from "../../hooks/useEngagementSync";
import NewMessageIndicator from "./NewMessageIndicator";

const CareTeamInbox: React.FC = () => {
  const router = useIonRouter();
  const { features, hasOrganization, refresh: refreshFeatures } = useEngagementFeatures();
  // ⛔ `checked_in_today` IS THE SERVER'S ANSWER ABOUT THE PATIENT'S OWN DAY (E12d/R3). This
  // block used to compare `checkin_date` against a date derived from the device clock, which
  // put "today's check-in" on the screen of a patient who had already answered.
  const { sync, isLoading, refresh: refreshSync } = useEngagementSync(hasOrganization);

  // F-514 — the notifications page is cached between visits; re-read the organization and its
  // switches when the patient comes back to it, so a messaging toggle (or an org switch) made
  // meanwhile is reflected rather than the row it used to allow.
  const enteredOnce = useRef(false);
  useIonViewWillEnter(() => {
    if (!enteredOnce.current) {
      enteredOnce.current = true;
      return;
    }
    void refreshFeatures();
    void refreshSync();
  });

  if (!hasOrganization || isLoading) return null;

  // ⛔ F-514 (UAT, Leah) — NO MESSAGES ENTRY WHILE THE ORGANIZATION HAS MESSAGING OFF.
  // `messaging_enabled` is the effective switch the kernel resolves for this patient and
  // enforces on every write (`MESSAGING_DISABLED`); a row leading to a conversation the patient
  // cannot reply in is exactly the entry the tester found.
  const showReplies = features.messaging_enabled && sync.unread_replies > 0;

  const nothingToSay =
    !showReplies && sync.surveys_outstanding === 0 && sync.checked_in_today;
  if (nothingToSay) return null;

  return (
    <div className="mb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-2 py-3 bg-cyan-50 border-b border-cyan-100">
          <h2 className="text-lg font-semibold text-cyan-800 flex items-center">
            <IonIcon icon={chatbubbleEllipsesOutline} className="mr-2" />
            From your care team
          </h2>
          <p className="text-sm text-cyan-700 mt-1">
            {features.org_name ?? "Your provider"}
            {features.location_name ? ` · ${features.location_name}` : ""}
          </p>
        </div>

        {showReplies && (
          <IonItem button detail onClick={() => router.push("/messages", "forward")}>
            <IonIcon icon={chatbubbleEllipsesOutline} slot="start" />
            <IonLabel>
              <h3>
                {sync.unread_replies} {sync.unread_replies === 1 ? "reply" : "replies"} from your
                care team
              </h3>
              {/* ⛔ NO MESSAGE TEXT HERE. URS_12.1 AC 5: a notification about a portal message
                  carries no PHI; the content is visible only after signing in and opening the
                  thread. A preview line would defeat that on a lock screen mirror. */}
              <IonNote>Open the conversation to read it.</IonNote>
            </IonLabel>
            {/* F-482 (UAT3): green "New", never a red count. */}
            <NewMessageIndicator count={sync.unread_replies} slot="end" />
          </IonItem>
        )}

        {sync.surveys_outstanding > 0 && (
          <IonItem button detail onClick={() => router.push("/surveys", "forward")}>
            <IonIcon icon={documentTextOutline} slot="start" />
            <IonLabel>
              <h3>
                {sync.surveys_outstanding} survey
                {sync.surveys_outstanding === 1 ? "" : "s"} waiting
              </h3>
              <IonNote>A few questions about how your therapy is going.</IonNote>
            </IonLabel>
          </IonItem>
        )}

        {!sync.checked_in_today && (
          <IonItem button detail onClick={() => router.push("/check-in", "forward")}>
            <IonIcon icon={happyOutline} slot="start" />
            <IonLabel>
              <h3>Today&rsquo;s check-in</h3>
              <IonNote>Let your care team know how you are feeling.</IonNote>
            </IonLabel>
          </IonItem>
        )}
      </div>
    </div>
  );
};

export default CareTeamInbox;
